import {
  IvrTargetKind,
  IvrTargetScope,
  ProviderRole,
  getProviderRoleAbbreviation,
  getProviderRoleLabel,
  CallRoutingSkipReason,
} from './constants';
import { IvrDigit, IvrKeyMapping, IvrTarget, blocksNamedProviderRung } from './ivr';

/**
 * The route tester (BAR-2624): given the chain currently in the admin form, who would the
 * runtime ring, and what does the team read afterwards.
 *
 * Everything here is pure. The caller supplies an {@link IvrRouteAvailability} snapshot —
 * gathered by the `ivr-route-availability` bot from the same Flag/Schedule/Slot data the
 * runtime reads — and gets back the resolved path. Nothing in this module can place a call
 * or write a resource, which is the point: the tester is a simulation.
 */

/** One person a rung would offer the call to. */
export interface RouteTestProvider {
  /** `Practitioner/<id>`. */
  reference: string;
  name: string;
  /**
   * Whether the runtime would ring them at the tested moment.
   *
   * For pool and care-team rungs this is `filterAvailableProviders`' verdict. For the
   * on-call rungs it means "has a phone number to dial", which is the only thing
   * `routeOnCallNp` checks.
   */
  available: boolean;
  /**
   * Which objection was raised, when there was one.
   *
   * A named-provider rung is gated on *which* verdict came back rather than on whether
   * one did, so "unavailable" alone cannot decide that rung.
   */
  unavailableReason?: CallRoutingSkipReason;
}

/** The on-call shift covering the tested moment, if any. */
export interface RouteTestOnCall {
  primary?: RouteTestProvider;
  backup?: RouteTestProvider;
}

/**
 * Who exists and who is reachable at the tested moment.
 *
 * Deliberately independent of the chain: it depends only on the caller and the time, so the
 * strip can re-resolve a freshly edited chain against a snapshot it already has, with no
 * round trip.
 */
export interface IvrRouteAvailability {
  /** Everyone holding each role, available or not. */
  pools: Partial<Record<ProviderRole, RouteTestProvider[]>>;
  /** The caller's care-team members by role. Empty for an unknown caller. */
  careTeam: Partial<Record<ProviderRole, RouteTestProvider[]>>;
  /** Absent when no on-call shift covers the tested moment. */
  onCall?: RouteTestOnCall;
  /** Providers named outright by a `specific-provider` rung, keyed by reference string. */
  namedProviders: Partial<Record<string, RouteTestProvider>>;
}

export const EMPTY_IVR_ROUTE_AVAILABILITY: IvrRouteAvailability = {
  pools: {},
  careTeam: {},
  namedProviders: {},
};

/** Who is calling. An absent `reference` is an unknown number, not a missing lookup. */
export interface RouteTestCaller {
  reference?: string;
  /** What to call them in the output — a patient name, or "Unknown number". */
  name: string;
}

export const RouteStepOutcome = {
  /** The first rung with somebody eligible. This is where the call goes. */
  REACHED: 'reached',
  /** Eligible, but only rung if nothing above it is answered. */
  NOT_REACHED: 'not-reached',
  /** Nobody eligible — the runtime skips it instantly, with no ring time spent. */
  SKIPPED: 'skipped',
  /**
   * An `owner` rung with no caller to resolve an owner against. Called out rather than
   * folded into SKIPPED: it is a property of the test, not of the configuration.
   */
  NO_CALLER: 'no-caller',
  /** A `play-and-hangup` rung. The call ends here by design; nothing below it runs. */
  ENDS_CALL: 'ends-call',
} as const;

export type RouteStepOutcome = (typeof RouteStepOutcome)[keyof typeof RouteStepOutcome];

export interface ResolvedRouteStep {
  target: IvrTarget;
  /** "CC pool", "PN care team", "On-call NP". */
  label: string;
  outcome: RouteStepOutcome;
  /** Everyone the rung would consider, whether or not they are reachable. */
  candidates: RouteTestProvider[];
  /** Who actually rings. A pool rings everyone in it at once (BAR-2618). */
  ringing: RouteTestProvider[];
  /** The number dialled, for an `external-number` rung. */
  externalNumber?: string;
  /** Why this rung ended up where it did, when that is not obvious from the outcome. */
  detail?: string;
  /** The team a missed-call row names for this rung. Absent for external and hang-up rungs. */
  notifyTeam?: string;
  /** Second person, for the sentence that team reads. "you're in the Care Coordinator pool". */
  whyPhrase?: string;
}

export const RouteTestStatus = {
  RESOLVED: 'resolved',
  /** No mapping on this digit, or a mapping whose chain is empty. */
  UNCONFIGURED: 'unconfigured',
  /** Configured but switched off. */
  DISABLED: 'disabled',
  /** Scoped to an hours set that is closed, with no after-hours chain. */
  CLOSED: 'closed',
} as const;

export type RouteTestStatus = (typeof RouteTestStatus)[keyof typeof RouteTestStatus];

/** One team the missed-call row reaches, and the sentence they read on it. */
export interface RouteTestNotifiedTeam {
  /** "the Care Coordinator pool", "the on-call NP", or a named provider. */
  team: string;
  /** Second person, as that team sees it in their inbox. */
  whyLine?: string;
}

export interface RouteTestResult {
  status: RouteTestStatus;
  /** "Menu 1 · English · after hours". */
  matchedAs: string;
  steps: ResolvedRouteStep[];
  /** True when no rung rings anybody. The tester's highest-value output. */
  reachesNobody: boolean;
  /** Set whenever nobody is rung — the line the strip leads with instead of a destination. */
  nobodyMessage?: string;
  /**
   * Everyone the chain attempts, in ring order. Every one of them gets the missed-call
   * row, not just the last (BAR-2618) — naming only the last under-reports who is on the
   * hook for the call, which is why the admin table's single-answer column was dropped.
   */
  notifiedTeams: RouteTestNotifiedTeam[];
}

export interface RouteTestRequest {
  digit: IvrDigit;
  /** The template being edited, e.g. "English". */
  menuLabel: string;
  mapping: IvrKeyMapping | undefined;
  caller: RouteTestCaller;
  /** Whether the tested moment falls outside the option's hours set. */
  isAfterHours: boolean;
  availability: IvrRouteAvailability;
}

/**
 * What the caller gets when no rung rings.
 *
 * This is today's behaviour, not BAR-2621's: `routeToProviderOrForward` forwards to the
 * virtual receptionist whenever it has no assignee. BAR-2621 replaces it with the optional
 * goodbye message; this string moves with it.
 */
const NOBODY_OUTCOME = 'the caller is forwarded to the virtual receptionist';

function roleRungLabel(role: ProviderRole, scope: IvrTargetScope): string {
  const abbreviation = getProviderRoleAbbreviation(role) || getProviderRoleLabel(role);
  return scope === IvrTargetScope.POOL ? `${abbreviation} pool` : `${abbreviation} care team`;
}

function roleNotifyTeam(role: ProviderRole, scope: IvrTargetScope): string {
  const label = getProviderRoleLabel(role) || role;
  return scope === IvrTargetScope.POOL ? `the ${label} pool` : `the ${label} on this patient's care team`;
}

function roleWhyPhrase(role: ProviderRole, scope: IvrTargetScope): string {
  const label = getProviderRoleLabel(role) || role;
  return scope === IvrTargetScope.POOL
    ? `you're in the ${label} pool`
    : `you're the ${label} on this patient's care team`;
}

function resolveRoleStep(
  target: Extract<IvrTarget, { kind: typeof IvrTargetKind.ROLE }>,
  caller: RouteTestCaller,
  availability: IvrRouteAvailability
): Omit<ResolvedRouteStep, 'outcome' | 'ringing'> & { eligible: RouteTestProvider[]; blocked?: string } {
  const isOwner = target.scope === IvrTargetScope.OWNER;
  const base = {
    target,
    label: roleRungLabel(target.role, target.scope),
    notifyTeam: roleNotifyTeam(target.role, target.scope),
    whyPhrase: roleWhyPhrase(target.role, target.scope),
  };

  // An owner rung has nothing to resolve against without a patient. The runtime's old
  // hidden pool fallback is now an explicit rung of its own (BAR-2619), so this rung is
  // genuinely skipped rather than quietly becoming a pool call.
  if (isOwner && !caller.reference) {
    return {
      ...base,
      candidates: [],
      eligible: [],
      blocked: 'No caller selected, so there is no care team to resolve an owner against.',
    };
  }

  const candidates = (isOwner ? availability.careTeam[target.role] : availability.pools[target.role]) ?? [];

  return { ...base, candidates, eligible: candidates.filter((provider) => provider.available) };
}

/**
 * Walks one chain and says what each rung does.
 *
 * Eligibility is decided per rung, independently of position: a rung with nobody eligible
 * is skipped whether it is first or last. The first eligible rung is where the call goes;
 * later eligible rungs are reached only if nothing above them is answered.
 */
export function resolveRouteChain(
  targets: IvrTarget[],
  caller: RouteTestCaller,
  availability: IvrRouteAvailability
): ResolvedRouteStep[] {
  const steps: ResolvedRouteStep[] = [];
  let callAlreadyPlaced = false;

  for (const target of targets) {
    // Everything below a play-and-hangup rung is unreachable: the call is already over.
    if (callAlreadyPlaced && target.kind === IvrTargetKind.PLAY_AND_HANGUP) {
      steps.push({
        target,
        label: 'Play a message and hang up',
        outcome: RouteStepOutcome.NOT_REACHED,
        candidates: [],
        ringing: [],
      });
      continue;
    }

    switch (target.kind) {
      case IvrTargetKind.ROLE: {
        const { eligible, blocked, ...step } = resolveRoleStep(target, caller, availability);

        if (blocked) {
          steps.push({ ...step, outcome: RouteStepOutcome.NO_CALLER, ringing: [], detail: blocked });
          break;
        }

        if (eligible.length === 0) {
          steps.push({
            ...step,
            outcome: RouteStepOutcome.SKIPPED,
            ringing: [],
            detail:
              step.candidates.length === 0
                ? 'Nobody holds this role here.'
                : `Nobody eligible — all ${step.candidates.length} are unavailable.`,
          });
          break;
        }

        steps.push({
          ...step,
          outcome: callAlreadyPlaced ? RouteStepOutcome.NOT_REACHED : RouteStepOutcome.REACHED,
          ringing: eligible,
        });
        callAlreadyPlaced = true;
        break;
      }

      case IvrTargetKind.ON_CALL_NP: {
        const onCall = availability.onCall;
        const candidates = [onCall?.primary, onCall?.backup].filter(
          (provider): provider is RouteTestProvider => !!provider
        );
        const step = {
          target,
          label: 'On-call NP',
          candidates,
          notifyTeam: 'the on-call NP',
          whyPhrase: 'you were the on-call NP',
        };

        const reachable = candidates.find((provider) => provider.available);

        if (!reachable) {
          steps.push({
            ...step,
            outcome: RouteStepOutcome.SKIPPED,
            ringing: [],
            detail:
              candidates.length === 0
                ? 'No on-call shift covers this time.'
                : 'The on-call NP has no phone number on file.',
          });
          break;
        }

        steps.push({
          ...step,
          outcome: callAlreadyPlaced ? RouteStepOutcome.NOT_REACHED : RouteStepOutcome.REACHED,
          ringing: [reachable],
          detail: reachable === onCall?.backup ? 'The primary has no phone number, so the backup takes it.' : undefined,
        });
        callAlreadyPlaced = true;
        break;
      }

      case IvrTargetKind.EXTERNAL_NUMBER: {
        const step = {
          target,
          label: target.phoneNumber ? `External number · ${target.phoneNumber}` : 'External number',
          candidates: [],
          ringing: [],
          externalNumber: target.phoneNumber,
        };

        if (!target.phoneNumber.trim()) {
          steps.push({ ...step, outcome: RouteStepOutcome.SKIPPED, detail: 'No number entered.' });
          break;
        }

        // Nothing on the other end of an external number is knowable from here, so it is
        // always offered — which is also what the runtime does.
        steps.push({
          ...step,
          outcome: callAlreadyPlaced ? RouteStepOutcome.NOT_REACHED : RouteStepOutcome.REACHED,
        });
        callAlreadyPlaced = true;
        break;
      }

      case IvrTargetKind.SPECIFIC_PROVIDER: {
        const reference = target.providerRef?.reference;
        const provider = reference ? availability.namedProviders[reference] : undefined;
        const step = {
          target,
          label: provider ? provider.name : 'Named provider',
          candidates: provider ? [provider] : [],
          notifyTeam: provider ? provider.name : undefined,
          whyPhrase: 'this option rings you by name',
        };

        if (!provider) {
          steps.push({
            ...step,
            outcome: RouteStepOutcome.SKIPPED,
            ringing: [],
            detail: 'That provider is no longer in the directory.',
          });
          break;
        }

        // Narrower than a pool rung's filter (BAR-2621): only the two verdicts about right
        // now skip a named person. `OFF_SHIFT` rings anyway, because it is also what "has
        // no call-availability Schedule at all" returns and a named target is the person
        // least likely to have one.
        if (blocksNamedProviderRung(provider.unavailableReason)) {
          steps.push({
            ...step,
            outcome: RouteStepOutcome.SKIPPED,
            ringing: [],
            detail:
              provider.unavailableReason === CallRoutingSkipReason.IN_ANOTHER_CALL
                ? 'Skipped — they are on another call.'
                : 'Skipped — they have do-not-disturb on.',
          });
          break;
        }

        steps.push({
          ...step,
          outcome: callAlreadyPlaced ? RouteStepOutcome.NOT_REACHED : RouteStepOutcome.REACHED,
          ringing: [provider],
          detail: provider.available
            ? undefined
            : 'Rung anyway — only an active call or do-not-disturb skips a rung that names somebody.',
        });
        callAlreadyPlaced = true;
        break;
      }

      case IvrTargetKind.PLAY_AND_HANGUP: {
        steps.push({
          target,
          label: 'Play a message and hang up',
          outcome: RouteStepOutcome.ENDS_CALL,
          candidates: [],
          ringing: [],
          detail: 'The call ends here. Nobody is rung and no missed-call row is created.',
        });
        callAlreadyPlaced = true;
        break;
      }
    }
  }

  // Everything below the rung that hangs up is unreachable in a stronger sense than an
  // unanswered rung is: the call is already over, so nothing below rings and there is no
  // missed call for anyone to pick up. The loop above resolves those rungs normally so their
  // labels and candidate lists still read correctly; this strips what would otherwise make
  // one of them the team a missed-call row names.
  const endsAt = steps.findIndex((step) => step.outcome === RouteStepOutcome.ENDS_CALL);

  if (endsAt < 0) {
    return steps;
  }

  return steps.map((step, index) =>
    index <= endsAt
      ? step
      : {
          ...step,
          outcome: RouteStepOutcome.NOT_REACHED,
          ringing: [],
          notifyTeam: undefined,
          whyPhrase: undefined,
        }
  );
}

function hoursSuffix(mapping: IvrKeyMapping, isAfterHours: boolean): string {
  if (!mapping.hoursSetId) {
    return 'around the clock';
  }
  return isAfterHours ? 'after hours' : 'in hours';
}

/**
 * The whole strip: which chain runs, who it rings, and what the team reads if nobody does.
 *
 * `isAfterHours` picks the chain and is ignored for an unscoped option, which routes the
 * same way at every hour.
 */
export function resolveRouteTest({
  digit,
  menuLabel,
  mapping,
  caller,
  isAfterHours,
  availability,
}: RouteTestRequest): RouteTestResult {
  const baseMatchedAs = `Menu ${digit} · ${menuLabel}`;

  if (!mapping) {
    return {
      status: RouteTestStatus.UNCONFIGURED,
      matchedAs: baseMatchedAs,
      steps: [],
      reachesNobody: true,
      notifiedTeams: [],
      nobodyMessage: 'Nothing is configured on this digit — the caller hears the invalid-key prompt.',
    };
  }

  const matchedAs = `${baseMatchedAs} · ${hoursSuffix(mapping, isAfterHours)}`;

  if (mapping.enabled === false) {
    return {
      status: RouteTestStatus.DISABLED,
      matchedAs,
      steps: [],
      reachesNobody: true,
      notifiedTeams: [],
      nobodyMessage: 'This option is switched off — the caller hears the invalid-key prompt.',
    };
  }

  const scoped = !!mapping.hoursSetId;
  const chain = scoped && isAfterHours ? (mapping.afterHoursTargets ?? []) : mapping.targets;

  if (scoped && isAfterHours && chain.length === 0) {
    return {
      status: RouteTestStatus.CLOSED,
      matchedAs,
      steps: [],
      reachesNobody: true,
      notifiedTeams: [],
      nobodyMessage: `Closed, and no after-hours chain is configured — ${NOBODY_OUTCOME}.`,
    };
  }

  if (chain.length === 0) {
    return {
      status: RouteTestStatus.UNCONFIGURED,
      matchedAs,
      steps: [],
      reachesNobody: true,
      notifiedTeams: [],
      nobodyMessage: 'This option has no rungs — the caller hears the invalid-key prompt.',
    };
  }

  const steps = resolveRouteChain(chain, caller, availability);
  const reachesNobody = !steps.some(
    (step) => step.outcome === RouteStepOutcome.REACHED || step.outcome === RouteStepOutcome.ENDS_CALL
  );

  if (reachesNobody) {
    return {
      status: RouteTestStatus.RESOLVED,
      matchedAs,
      steps,
      reachesNobody: true,
      notifiedTeams: [],
      nobodyMessage: `Nobody — every rung on this chain is skipped, so ${NOBODY_OUTCOME}.`,
    };
  }

  // Every rung the call is offered to, in ring order. A chain that runs all the way out has
  // attempted all of them, and BAR-2618 puts the missed-call row in each of their inboxes —
  // so the tester names all of them rather than only the team left holding the call.
  const notifiedTeams: RouteTestNotifiedTeam[] = [];
  const seen = new Set<string>();

  for (const step of steps) {
    const isOffered = step.outcome === RouteStepOutcome.REACHED || step.outcome === RouteStepOutcome.NOT_REACHED;

    if (!isOffered || !step.notifyTeam || seen.has(step.notifyTeam)) {
      continue;
    }

    seen.add(step.notifyTeam);
    notifiedTeams.push({
      team: step.notifyTeam,
      whyLine: step.whyPhrase ? `You're seeing this because ${step.whyPhrase} and nobody answered.` : undefined,
    });
  }

  return {
    status: RouteTestStatus.RESOLVED,
    matchedAs,
    steps,
    reachesNobody: false,
    notifiedTeams,
  };
}

/** Every role a chain references, so the availability snapshot only reads the pools in play. */
export function collectRouteTestRoles(mappings: IvrKeyMapping[]): ProviderRole[] {
  const roles = new Set<ProviderRole>();

  for (const target of allTargets(mappings)) {
    if (target.kind === IvrTargetKind.ROLE) {
      roles.add(target.role);
    }
  }

  return [...roles].sort();
}

/** Every provider named outright by a chain, for the same reason. */
export function collectRouteTestProviderRefs(mappings: IvrKeyMapping[]): string[] {
  const references = new Set<string>();

  for (const target of allTargets(mappings)) {
    if (target.kind === IvrTargetKind.SPECIFIC_PROVIDER && target.providerRef?.reference) {
      references.add(target.providerRef.reference);
    }
  }

  return [...references].sort();
}

function allTargets(mappings: IvrKeyMapping[]): IvrTarget[] {
  return mappings.flatMap((mapping) => [...mapping.targets, ...(mapping.afterHoursTargets ?? [])]);
}
